import {
  DefaultAlertDialog,
  DefaultModalDialog,
  DurationViewer,
  Heading,
  Image,
  LabeledText,
  Link,
  Select,
  Option,
  Text,
  useToast,
} from '~/common/components/defaultUIComponents';
import {
  Box,
  Flex,
  Grid,
  GridItem,
  SimpleGrid,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import ProjectApi from '~/common/api/zeiterfassungServerApi/ProjectApi';
import { useCopyJobToProject, useDeleteJob, useProject } from './utils';
import { CopyIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { Job } from '~/common/entities/Project/ProjectTypes';
import { JobListPhotoModal } from './JobListPhotoModal';
import routes from '~/common/constants/routes.json';
import useTranslate from '~/common/utils/translate';
import { Paper } from '~/common/components/Paper';
import { useNavigate } from 'react-router-dom';
import React, { useState } from 'react';
import _ from 'lodash';

interface JobListItemProps {
  job: Job;
}

export const JobListItem: React.FC<JobListItemProps> = ({ job }) => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const [{ data: projects }] = ProjectApi.hooks.useProjectsQuery();
  const { project } = useProject();
  const [newProjectID, setNewProjectID] = useState(project?.id ?? '');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { copyOpen, setCopyOpen, copyJob } = useCopyJobToProject();
  const { confirmOpen, setConfirmOpen, deleteJob } = useDeleteJob(job);

  const thumbnail = React.useMemo(() => {
    if (job.job_pictures?.length) {
      return `/images/job/${_.first(job.job_pictures)?.id ?? ''}`;
    }
    return null;
  }, [job]);

  const toast = useToast({});
  return (
    <>
      <DefaultModalDialog
        dialogHeaderText="jobListItem.duplicateProject.modalHeader"
        submitText="projectTile.duplicateProject.submitText"
        isOpen={copyOpen}
        onCancel={() => setCopyOpen(false)}
        disableSubmitButton={newProjectID === ''}
        onSubmit={() =>
          copyJob({ new_project_id: newProjectID, job_id: job.id })
        }
        showCancelButton
        size="xl"
        initialValues={{ newProjectID: '' }}
      >
        <Select
          value={newProjectID}
          placeholder={translate('jobListItem.duplicateJob.InputText')}
          onChange={(e) => setNewProjectID(e.currentTarget.value)} //}
          w="100%"
          ml={60}
          mb={2}
        >
          {projects &&
            _.orderBy(projects, ['name'], ['asc']).map((p) => (
              <Option key={p.id} value={p.id}>
                {p.name}
              </Option>
            ))}
        </Select>
      </DefaultModalDialog>
      <DefaultAlertDialog
        dialogHeaderText="jobTile.deleteJob.dialogHeaderText"
        dialogBodyText="jobTile.deleteJob.dialogBodyText"
        isOpen={confirmOpen}
        onConfirmedAction={() => {
          deleteJob().catch(() =>
            toast({ title: 'generic.error', status: 'error' }),
          );
        }}
        onCancel={() => setConfirmOpen(false)}
      />
      <Paper transitionDuration="1000" _hover={{ shadow: 'xl' }} w="full">
        <Flex justify="space-between" alignContent="center">
          <SimpleGrid columns={3} gap={2}>
            <Link
              href={routes.EDIT_JOB.replace(':id', job.id as unknown as string)}
            >
              <Heading fontSize="large">{job.name}</Heading>
            </Link>
            <Text params={[`${job.step_number}`]}>
              jobTile.stepNumber.label
            </Text>
          </SimpleGrid>
          <SimpleGrid columns={3} columnGap={3}>
            <Tooltip
              label={translate('jobListItem.duplicateProject.tooltip')}
              aria-label={translate('jobListItem.duplicateProject.tooltip')}
            >
              <Box
                _hover={{ cursor: 'pointer' }}
                onClick={() => setCopyOpen(true)}
              >
                <CopyIcon />
              </Box>
            </Tooltip>
            <Tooltip
              label={translate('jobListItem.editProject.tooltip')}
              aria-label={translate('jobListItem.editProject.tooltip')}
            >
              <Box
                _hover={{ cursor: 'pointer' }}
                onClick={() =>
                  navigate(
                    routes.EDIT_JOB.replace(':id', job.id as unknown as string),
                  )
                }
              >
                <EditIcon />
              </Box>
            </Tooltip>
            <Tooltip
              label={translate('jobListItem.deleteProject.tooltip.label')}
              aria-label={translate('jobListItem.deleteProject.tooltip.label')}
            >
              <Box
                _hover={{ cursor: 'pointer' }}
                onClick={() => setConfirmOpen(true)}
              >
                <DeleteIcon color="red.600" />
              </Box>
            </Tooltip>
          </SimpleGrid>
        </Flex>
        <Grid templateColumns="repeat(4, 1fr)" gap={1}>
          <GridItem>
            <DurationViewer
              durationInSeconds={job.duration as number}
              showSeconds
              label="jobTile.duration.label"
              mr={5}
            />
          </GridItem>
          <GridItem>
            <LabeledText label="jobTile.donePerProject.label">
              {job.done_per_project > 0 ? job.done_per_project : 0}
            </LabeledText>
          </GridItem>

          <GridItem>
            {!!job.department && job.department.name !== '' ? (
              <LabeledText label="jobTile.department.label">
                {job.department?.name}
              </LabeledText>
            ) : null}
          </GridItem>
          <GridItem colSpan={1} rowSpan={2} display="flex" justifyContent="end">
            {thumbnail && (
              <Image
                _hover={{ cursor: 'pointer' }}
                src={thumbnail}
                maxWidth={200}
                maxHeight={200}
                onClick={onOpen}
              />
            )}
            <JobListPhotoModal
              job={job}
              onClose={onClose}
              isOpen={isOpen}
              projectID={project?.id}
            />
          </GridItem>
          {job.description && (
            <GridItem colSpan={3}>
              <LabeledText col label="jobTile.description.label">
                {job.description}
              </LabeledText>
            </GridItem>
          )}
          {job.important_message && (
            <GridItem colSpan={3}>
              <LabeledText col label="jobTile.importantMessage.label">
                {job.important_message}
              </LabeledText>
            </GridItem>
          )}
        </Grid>
      </Paper>
    </>
  );
};
